import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Panel views (plan §3.3): `'home'`, `'draft'` (a new conversation the
 * server doesn't know yet) or `{ conversationId }`.
 */
export const PANEL_VIEWS = Object.freeze({ HOME: "home", DRAFT: "draft" });

const STORAGE_PREFIX = "aiSupport.panelView.";
const MAX_ID_LENGTH = 128;

/** @returns {boolean} the view shows one saved conversation */
export const isConversationView = (view) =>
  typeof view?.conversationId === "string";

const isValidId = (id) =>
  typeof id === "string" && id.length > 0 && id.length <= MAX_ID_LENGTH;

/** `sessionStorage`, or null where reading the property itself throws. */
const getSessionStorage = () => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

/**
 * The last view of this profile, or null when nothing valid is stored. The
 * key carries the profile id, so another user signing in on the same tab
 * never reopens a foreign conversation (C16).
 *
 * @param {Storage | null} storage
 * @param {string | null | undefined} profileId
 * @returns {'home' | { conversationId: string } | null}
 */
export const readStoredView = (storage, profileId) => {
  if (!storage || !profileId) return null;
  try {
    const stored = JSON.parse(storage.getItem(STORAGE_PREFIX + profileId));
    if (stored === PANEL_VIEWS.HOME) return PANEL_VIEWS.HOME;
    if (isValidId(stored?.conversationId)) {
      return { conversationId: stored.conversationId };
    }
  } catch {
    // Blocked storage or a corrupt value: start from home
  }
  return null;
};

/**
 * Remembers the view for this profile. A draft is stored as home: nothing of
 * it exists on the server, so a reload can't bring it back anyway.
 *
 * @param {Storage | null} storage
 * @param {string | null | undefined} profileId
 * @param {'home' | 'draft' | { conversationId: string }} view
 */
export const writeStoredView = (storage, profileId, view) => {
  if (!storage || !profileId) return;
  const stored = isConversationView(view)
    ? { conversationId: view.conversationId }
    : PANEL_VIEWS.HOME;
  try {
    storage.setItem(STORAGE_PREFIX + profileId, JSON.stringify(stored));
  } catch {
    // Quota or blocked storage: the panel works without memory
  }
};

/**
 * Open state and current view of the chat panel. The view survives a reload
 * (sessionStorage, per profile); the open state doesn't, so a reload never
 * pops the panel over the page.
 *
 * @param {string | null | undefined} profileId
 * @returns {{
 *   isOpen: boolean, open: () => void, close: () => void, toggle: () => void,
 *   view: 'home' | 'draft' | { conversationId: string },
 *   showHome: () => void, showDraft: () => void,
 *   showConversation: (id: string) => void,
 * }}
 */
export default function useAiChatPanel(profileId) {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState(
    () => readStoredView(getSessionStorage(), profileId) ?? PANEL_VIEWS.HOME
  );

  useEffect(() => {
    writeStoredView(getSessionStorage(), profileId, view);
  }, [profileId, view]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((wasOpen) => !wasOpen), []);
  const showHome = useCallback(() => setView(PANEL_VIEWS.HOME), []);
  const showDraft = useCallback(() => setView(PANEL_VIEWS.DRAFT), []);
  const showConversation = useCallback((id) => {
    if (!isValidId(id)) return;
    // Same id → same object, so the conversation view doesn't remount
    setView((current) =>
      current?.conversationId === id ? current : { conversationId: id }
    );
  }, []);

  return useMemo(
    () => ({
      isOpen,
      open,
      close,
      toggle,
      view,
      showHome,
      showDraft,
      showConversation,
    }),
    [isOpen, open, close, toggle, view, showHome, showDraft, showConversation]
  );
}
